import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const tenantTypeDetails = {
  legal: { label: 'Legal', users: 'Users: Advocate, Reviewer, Tenant Admin', badge: 'bg-blue-100 text-blue-800' },
  cs: { label: 'CS', users: 'Users: CS, CS Approver, Tenant Admin', badge: 'bg-purple-100 text-purple-800' },
  brd: { label: 'BRD', users: 'Users: Builder, Realtor, Developer, Tenant Admin', badge: 'bg-rose-100 text-rose-800' },
};

const TenantManagement = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    plan: 'free',
    tenant_type: 'legal',
    admin_email: '',
    admin_password: '',
    admin_name: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    domain: '',
    plan: 'free',
    tenant_type: 'legal',
    settings: {}
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    // Check for token first
    if (!token) {
      alert('Please login first');
      navigate('/login');
      return;
    }
    fetchTenants();
  }, [token, navigate]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${backendUrl}/api/admin/tenants`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Tenants fetched:', response.data);
      setTenants(response.data);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      console.error('Error response:', err.response);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to fetch tenants';
      setError(errorMsg);
      if (err.response?.status === 403 || err.response?.status === 401) {
        alert('Access denied. You need super admin privileges.');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const response = await axios.post(`${backendUrl}/api/admin/tenants`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Tenant created:', response.data);
      alert('Tenant created successfully!');
      setShowCreateForm(false);
      setFormData({ name: '', domain: '', plan: 'free', tenant_type: 'legal', admin_email: '', admin_password: '', admin_name: '' });
      fetchTenants();
    } catch (err) {
      console.error('Error creating tenant:', err);
      console.error('Error response:', err.response);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to create tenant';
      setError(errorMsg);
      alert(errorMsg);
    }
  };

  const handleStatusChange = async (tenantId, newStatus) => {
    if (tenantId === 'default') {
      alert('Cannot modify default tenant status');
      return;
    }

    const confirmMsg = newStatus === 'active' 
      ? 'Are you sure you want to enable this tenant?' 
      : 'Are you sure you want to disable this tenant? Users will not be able to access their data.';
    
    if (!window.confirm(confirmMsg)) return;

    try {
      await axios.patch(
        `${backendUrl}/api/admin/tenants/${tenantId}`,
        { status: newStatus },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      alert(`Tenant ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully!`);
      fetchTenants();
    } catch (err) {
      console.error('Error updating tenant:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to update tenant';
      alert(errorMsg);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (tenantId === 'default') {
      alert('Cannot delete default tenant');
      return;
    }

    if (!window.confirm('⚠️ WARNING: This will permanently delete the tenant and ALL associated data (users, documents, opinions). This action cannot be undone. Are you absolutely sure?')) {
      return;
    }

    try {
      await axios.delete(
        `${backendUrl}/api/admin/tenants/${tenantId}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      alert('Tenant deleted successfully!');
      fetchTenants();
    } catch (err) {
      console.error('Error deleting tenant:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to delete tenant';
      alert(errorMsg);
    }
  };

  const handleEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setEditFormData({
      name: tenant.name || '',
      domain: tenant.domain || '',
      plan: tenant.subscription_plan || tenant.plan || 'free',
      tenant_type: tenant.tenant_type || 'legal',
      settings: tenant.settings || {}
    });
    setShowEditModal(true);
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    if (!editingTenant) return;

    try {
      setError(null);
      await axios.put(
        `${backendUrl}/api/admin/tenants/${editingTenant.id}`,
        {
          name: editFormData.name,
          domain: editFormData.domain,
          subscription_plan: editFormData.plan,
          tenant_type: editFormData.tenant_type,
          settings: editFormData.settings
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      alert('Tenant updated successfully!');
      setShowEditModal(false);
      setEditingTenant(null);
      fetchTenants();
    } catch (err) {
      console.error('Error updating tenant:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to update tenant';
      setError(errorMsg);
      alert(errorMsg);
    }
  };

  if (error && !tenants.length) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-red-800 font-semibold mb-2">Error</h2>
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Tenant Management</h1>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {showCreateForm ? 'Cancel' : 'Create Tenant'}
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back
            </button>
          </div>
        </div>

        {/* Create Tenant Form */}
        {showCreateForm && (
          <div className="bg-white p-6 rounded-lg shadow mb-6" data-testid="tenant-create-form">
            <h2 className="text-xl font-semibold mb-4">Create New Tenant</h2>
            <form onSubmit={handleCreateTenant} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Domain (Optional)</label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <select
                    value={formData.plan}
                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Type</label>
                  <select
                    value={formData.tenant_type}
                    onChange={(e) => setFormData({ ...formData, tenant_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    data-testid="create-tenant-type-select"
                  >
                    <option value="legal">Legal (Advocates)</option>
                    <option value="cs">CS (Company Secretary)</option>
                    <option value="brd">BRD (Builder, Realtor, Developer)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {tenantTypeDetails[formData.tenant_type].users}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name</label>
                  <input
                    type="text"
                    required
                    value={formData.admin_name}
                    onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
                  <input
                    type="email"
                    required
                    value={formData.admin_email}
                    onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
                  <input
                    type="password"
                    required
                    value={formData.admin_password}
                    onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Create Tenant
              </button>
            </form>
          </div>
        )}

        {/* Tenants List */}
        <div className="bg-white rounded-lg shadow" data-testid="tenant-list">
          {loading ? (
            <div className="p-6 text-center">Loading...</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase sm:px-6">Name</th>
                  <th className="hidden px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase lg:table-cell sm:px-6">Domain</th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase sm:px-6">Type</th>
                  <th className="hidden px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase lg:table-cell sm:px-6">Plan</th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase sm:px-6">Status</th>
                  <th className="hidden px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase md:table-cell sm:px-6">Created</th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase sm:px-6">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tenants.map((tenant) => (
                  <tr key={tenant.id}>
                    <td className="break-words px-2 py-4 font-medium sm:px-6">{tenant.name}</td>
                    <td className="hidden break-words px-2 py-4 lg:table-cell sm:px-6">{tenant.domain || '-'}</td>
                    <td className="px-2 py-4 sm:px-6">
                      <span className={`px-2 py-1 text-xs rounded ${tenantTypeDetails[tenant.tenant_type]?.badge || tenantTypeDetails.legal.badge}`} data-testid={`tenant-type-badge-${tenant.id}`}>
                        {tenantTypeDetails[tenant.tenant_type]?.label || tenantTypeDetails.legal.label}
                      </span>
                    </td>
                    <td className="hidden px-2 py-4 lg:table-cell sm:px-6">
                      <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">
                        {tenant.plan}
                      </span>
                    </td>
                    <td className="px-2 py-4 sm:px-6">
                      <span className={`px-2 py-1 text-xs rounded ${
                        tenant.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {tenant.status}
                      </span>
                    </td>
                    <td className="hidden px-2 py-4 text-sm text-gray-500 md:table-cell sm:px-6">
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-2 py-4 text-sm sm:px-6">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleEditTenant(tenant)}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                        >
                          Edit
                        </button>
                        {tenant.status === 'active' ? (
                          <button
                            onClick={() => handleStatusChange(tenant.id, 'suspended')}
                            className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-xs"
                            disabled={tenant.id === 'default'}
                            title={tenant.id === 'default' ? 'Cannot disable default tenant' : 'Disable tenant'}
                          >
                            Disable
                          </button>
                        ) : (
                          <button
                            onClick={() => handleStatusChange(tenant.id, 'active')}
                            className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-xs"
                          >
                            Enable
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteTenant(tenant.id)}
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                          disabled={tenant.id === 'default'}
                          title={tenant.id === 'default' ? 'Cannot delete default tenant' : 'Delete tenant'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Edit Tenant Modal */}
        {showEditModal && editingTenant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Edit Tenant: {editingTenant.name}</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTenant(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleUpdateTenant} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tenant Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Domain (Optional)
                    </label>
                    <input
                      type="text"
                      value={editFormData.domain}
                      onChange={(e) => setEditFormData({ ...editFormData, domain: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subscription Plan *
                    </label>
                    <select
                      value={editFormData.plan}
                      onChange={(e) => setEditFormData({ ...editFormData, plan: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="free">Free</option>
                      <option value="basic">Basic</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tenant Type *
                    </label>
                    <select
                      value={editFormData.tenant_type}
                      onChange={(e) => setEditFormData({ ...editFormData, tenant_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid="edit-tenant-type-select"
                    >
                      <option value="legal">Legal (Advocates)</option>
                      <option value="cs">CS (Company Secretary)</option>
                      <option value="brd">BRD (Builder, Realtor, Developer)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {tenantTypeDetails[editFormData.tenant_type].users}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tenant ID (Read-only)
                    </label>
                    <input
                      type="text"
                      value={editingTenant.id}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Changes will be applied immediately. This will affect all users in this tenant.
                  </p>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTenant(null);
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantManagement;
